/**
 * Map a backend request failure to a user-facing message + a transient flag.
 *
 * HttpStorageAdapter throws `Error('API request failed: <status> <statusText>')`
 * for non-2xx responses and a `TypeError('Failed to fetch')`-style error for
 * network/abort/CORS failures (no status). Turning that into a generic
 * "Failed to X" alert hides the one thing the user needs to know: is it worth
 * trying again (server busy / offline) or is something actually wrong
 * (session expired / duplicate / rejected)?
 */

export interface BackendErrorInfo {
  /** True when retrying is likely to succeed (5xx, 429, network). */
  transient: boolean;
  /** A message safe to show directly to the user. */
  message: string;
}

/**
 * @param action a verb phrase describing the attempted operation, e.g.
 *   "create the folder" — inlined into the message.
 */
export function describeBackendError(
  error: unknown,
  action: string
): BackendErrorInfo {
  const raw = error instanceof Error ? error.message : String(error);
  const statusMatch = raw.match(/API request failed:\s*(\d{3})/);
  const status = statusMatch ? Number(statusMatch[1]) : null;

  if (status === 409) {
    return { transient: false, message: `Couldn’t ${action}: it already exists.` };
  }
  if (status === 401 || status === 403) {
    return {
      transient: false,
      message: `Couldn’t ${action}: your session may have expired. Sign in again.`,
    };
  }
  if (status === 400 || status === 404 || status === 422) {
    return {
      transient: false,
      message: `Couldn’t ${action}: the request was rejected.`,
    };
  }
  if (status === 429 || (status !== null && status >= 500)) {
    return {
      transient: true,
      message: `Couldn’t ${action} — the server is busy. Please try again in a moment.`,
    };
  }
  // No status parsed → almost always a network / abort / CORS failure.
  return {
    transient: true,
    message: `Couldn’t ${action} — check your connection and try again.`,
  };
}
